// Pure helpers for the ColormapBinding registry (Stage 2 — TASKS-UI.md).
//
// The registry lives at `params.bindings` (keyed by binding id) plus
// `params.bindingOrder` (creation order, drives colorbar stack order) and
// `params.nextBindingSeq` (id counter). Each primitive carries a
// `<primitive>ColormapId` reference into `params.bindings`.
//
// Subscriptions: callers listen on `params.bindingOrder` for "registry
// shape changed" events (add/remove) and on `params.bindings.<id>` for
// individual binding edits. The store's path-prefix notify makes both fire
// naturally when we mutate via `store.set`. For removes, we mutate state
// in place (no "unset" exists) and then publish `bindingOrder` to fan out.

import { params, store } from './instance';
import type {
  ColormapBinding,
  ColormapBindingId,
  PrimitiveKey,
} from './schema';

const DEFAULT_LUT = 'Plasma';
const DEFAULT_LOG_FLOOR = 1e-3;

/** Path to the per-primitive id field on params. */
const PRIMITIVE_ID_KEY: Record<PrimitiveKey, 'slicesColormapId' | 'volumeColormapId' | 'contourColormapId' | 'fieldlinesColormapId'> = {
  slices: 'slicesColormapId',
  volume: 'volumeColormapId',
  contour: 'contourColormapId',
  fieldlines: 'fieldlinesColormapId',
};

export function primitiveIdKey(primitive: PrimitiveKey): typeof PRIMITIVE_ID_KEY[PrimitiveKey] {
  return PRIMITIVE_ID_KEY[primitive];
}

export interface CreateBindingOpts {
  field: string;
  seed?: Partial<Omit<ColormapBinding, 'id'>>;
  bindTo?: ReadonlyArray<PrimitiveKey>;
}

/**
 * Mint a new binding. Returns its id. Does NOT call syncColormap — the
 * intent handler is responsible for re-firing the render side once it's
 * decided which primitives are affected.
 */
export function createBinding(opts: CreateBindingOpts): ColormapBindingId {
  const seq = params.nextBindingSeq;
  const id: ColormapBindingId = `cm-${seq}`;
  store.set('params.nextBindingSeq', seq + 1);

  const seed = opts.seed ?? {};
  const binding: ColormapBinding = {
    id,
    field: opts.field,
    colormap: seed.colormap ?? DEFAULT_LUT,
    min: typeof seed.min === 'number' ? seed.min : 0,
    max: typeof seed.max === 'number' ? seed.max : 1,
    log: !!seed.log,
    logFloor: typeof seed.logFloor === 'number' ? seed.logFloor : DEFAULT_LOG_FLOOR,
    units: seed.units ?? '',
    centerOnZero: !!seed.centerOnZero,
    transparentBounds: !!seed.transparentBounds,
  };

  store.set(`params.bindings.${id}`, binding);
  store.set('params.bindingOrder', [...params.bindingOrder, id]);

  if (opts.bindTo && opts.bindTo.length) {
    for (const p of opts.bindTo) repointPrimitive(p, id);
  }

  return id;
}

/** Apply a partial patch to an existing binding. */
export function updateBindingFields(id: ColormapBindingId, patch: Partial<Omit<ColormapBinding, 'id'>>): void {
  const cur = params.bindings[id];
  if (!cur) return;
  // store.set on a plain (non-array) object shallow-merges into existing.
  store.set(`params.bindings.${id}`, { ...patch });
}

/**
 * Remove a binding. Any primitive that referenced it falls back to a
 * sibling binding on the same field if one exists, else null.
 */
export function removeBindingById(id: ColormapBindingId): void {
  if (!params.bindings[id]) return;

  // Pick fallback: any other binding on the same field.
  const removedField = params.bindings[id]!.field;
  const fallback = params.bindingOrder.find(
    (other) => other !== id && params.bindings[other]?.field === removedField,
  ) ?? null;

  // Repoint dangling primitives BEFORE we drop the binding.
  for (const p of Object.keys(PRIMITIVE_ID_KEY) as PrimitiveKey[]) {
    if (params[PRIMITIVE_ID_KEY[p]] === id) repointPrimitive(p, fallback);
  }

  // Direct mutation: the store has no "unset", and leaving an `undefined`
  // entry under `params.bindings[id]` would clutter devtools.
  const bindings = store.state.params.bindings as Record<string, ColormapBinding>;
  delete bindings[id];

  // Publish the order change — the subscribers re-walk and notice the
  // dropped id.
  store.set('params.bindingOrder', params.bindingOrder.filter((x) => x !== id));

  // Trim visibleColorbarIds too — UI state, but we own its consistency here.
  if (params.visibleColorbarIds.includes(id)) {
    store.set(
      'params.visibleColorbarIds',
      params.visibleColorbarIds.filter((x) => x !== id),
    );
  }
}

/** Repoint a single primitive at a binding (or unbind with `null`). */
export function repointPrimitive(primitive: PrimitiveKey, id: ColormapBindingId | null): void {
  const key = PRIMITIVE_ID_KEY[primitive];
  if (params[key] === id) return;
  store.set(`params.${key}`, id);
}

/** First binding on `field`, or `null` if none. */
export function findBindingByField(field: string): ColormapBindingId | null {
  for (const id of params.bindingOrder) {
    if (params.bindings[id]?.field === field) return id;
  }
  return null;
}

/**
 * Return the binding for `field` if it exists, else mint one. Used by
 * auto-share whenever a primitive's target field changes.
 */
export function findOrCreateBindingForField(field: string, seed?: Partial<Omit<ColormapBinding, 'id'>>): ColormapBindingId {
  const hit = findBindingByField(field);
  if (hit) return hit;
  return createBinding({ field, seed: seed ?? {} });
}

/** Set of binding ids currently referenced by at least one primitive. */
export function activeBindingIds(): ReadonlyArray<ColormapBindingId> {
  const out: ColormapBindingId[] = [];
  const seen = new Set<ColormapBindingId>();
  const refs: Array<ColormapBindingId | null> = [
    params.slicesColormapId,
    params.volumeColormapId,
    params.contourColormapId,
    params.fieldlinesColormapId,
  ];
  for (const id of refs) {
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Primitives that currently point at this binding. */
export function primitivesReferencing(id: ColormapBindingId): ReadonlyArray<PrimitiveKey> {
  const out: PrimitiveKey[] = [];
  for (const p of Object.keys(PRIMITIVE_ID_KEY) as PrimitiveKey[]) {
    if (params[PRIMITIVE_ID_KEY[p]] === id) out.push(p);
  }
  return out;
}

/**
 * Repoint every primitive that currently references `sourceId` onto
 * `targetId`, then GC the now-unreferenced source. The Stage 2
 * "share into another binding" action — surfaces in the colorbar stack
 * chip row when more than two bindings are active.
 */
export function mergeBindings(sourceId: ColormapBindingId, targetId: ColormapBindingId): void {
  if (sourceId === targetId) return;
  if (!params.bindings[sourceId] || !params.bindings[targetId]) return;
  for (const p of primitivesReferencing(sourceId)) {
    repointPrimitive(p, targetId);
  }
  gcUnreferencedBindings();
}

/**
 * Drop any binding with zero references. Call after a repoint when the
 * intent is "consolidate", not when the user is just navigating bindings
 * (e.g. visibility toggles). Bindings that no primitive owns serve no
 * purpose; cleaning them keeps the warning math honest.
 */
export function gcUnreferencedBindings(): void {
  const referenced = new Set<ColormapBindingId>(activeBindingIds());
  for (const id of [...params.bindingOrder]) {
    if (!referenced.has(id)) removeBindingById(id);
  }
}
